/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react'

import { t } from '@/i18n'
import { Maybe } from '@/types'
import { CarNode } from '@/apollo/graphql'
import { editCarPath, generatePath } from '@/utils'
import { FieldViewLink, OptionSelector } from '@/components'
import { useCarSelect } from './useCarSelect'

interface Props {
  isEditing?: boolean
  initial?: Maybe<Partial<CarNode>>
  selected?: string
  fallbackEl?: (props: any) => React.ReactNode
  [x: string]: any
}

const CarSelector: React.FC<Props> = ({
  isEditing,
  initial,
  selected,
  fallbackEl,
  ...rest
}) => {
  const { carOptions, carsLoading, pageInfo, onFetchMore, onSearch } =
    useCarSelect({
      initial,
    })

  const selectedCar = useMemo(() => {
    if (selected) {
      return carOptions.find(c => c.value === selected)
    }

    return null
  }, [carOptions, selected])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  if (!isEditing) {
    if (selectedCar) {
      return (
        <FieldViewLink
          label={t('car')}
          linkLabel={selectedCar?.label}
          link={generatePath(editCarPath, {
            id: selectedCar?.value,
          })}
        />
      )
    } else if (fallbackEl) {
      return <>{fallbackEl(rest)}</>
    }
  }

  return (
    <OptionSelector
      {...rest}
      isSearchable
      isAsync
      name="car"
      menuPlacement="bottom"
      optionsLoading={carsLoading}
      options={carOptions}
      emptyValue={emptyValue}
      disabled={!Boolean(isEditing)}
      label={t('car')}
      fetchMoreStatus={pageInfo.fetchMoreStatus}
      onFetchMore={onFetchMore}
      onSearch={onSearch}
    />
  )
}

export default CarSelector
